import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Printer, Download, ExternalLink } from 'lucide-react';

const SATISFIND_LOGO = 'https://customer-assets.emergentagent.com/job_restaurant-voice/artifacts/726h775e_satisfind-notagline.png';

export default function DemoQRCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const printRef = useRef(null);

  const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      const res = await fetch(`${API}/demo/qr-codes`);
      const data = await res.json();
      setCodes(data.codes || []);
    } catch (err) {
      console.error('Failed to fetch codes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Group codes by branch
  const byBranch = {};
  codes.forEach(c => {
    if (!byBranch[c.branch_id]) {
      byBranch[c.branch_id] = { name: c.branch_name, store_code: c.store_code, codes: [] };
    }
    byBranch[c.branch_id].codes.push(c);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#D62B27] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header - hidden on print */}
      <div className="bg-[#D62B27] text-white px-6 py-4 print:hidden sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={SATISFIND_LOGO} alt="satisFIND" className="h-8" />
            <div>
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Demo QR Codes</h1>
              <p className="text-white/70 text-sm">{codes.length} codes across {Object.keys(byBranch).length} branches</p>
            </div>
          </div>
          <Button onClick={handlePrint} variant="outline" className="text-white border-white/30 hover:bg-white/10 hover:text-white" data-testid="print-btn">
            <Printer className="w-4 h-4 mr-2" />
            Print All
          </Button>
        </div>
      </div>

      {/* QR Code Grid */}
      <div className="max-w-6xl mx-auto px-6 py-8" ref={printRef}>
        {Object.entries(byBranch).map(([branchId, branch]) => (
          <div key={branchId} className="mb-10">
            <h2 className="text-lg font-bold text-[#0F172A] mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {branch.name}
            </h2>
            <p className="text-sm text-[#64748B] mb-4">Store Code: {branch.store_code}</p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {branch.codes.map((code) => {
                const surveyUrl = `${baseUrl}/survey/${code.branch_id}/${code.code}`;
                return (
                  <div 
                    key={code.code} 
                    className="bg-white rounded-xl p-6 shadow-sm border border-[#E2E8F0] text-center print:break-inside-avoid print:shadow-none print:border print:rounded-none"
                    data-testid={`qr-card-${code.code}`}
                  >
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <img src={SATISFIND_LOGO} alt="satisFIND" className="h-5" />
                    </div>
                    
                    <div className="flex justify-center mb-3">
                      <QRCodeSVG 
                        value={surveyUrl} 
                        size={160}
                        level="M"
                        includeMargin={true}
                        fgColor="#1A1A2E"
                      />
                    </div>
                    
                    <p className="text-xs font-mono text-[#64748B] mb-1 break-all">{code.code}</p>
                    <p className="text-[10px] text-[#94A3B8] mb-3">{branch.name}</p>
                    
                    <p className="text-xs text-[#0F172A] font-medium mb-1">Scan to share your feedback!</p>
                    <p className="text-[10px] text-[#94A3B8]">Powered by satisFIND</p>

                    {/* Open link - hidden on print */}
                    <div className="mt-3 print:hidden">
                      <a 
                        href={`/survey/${code.branch_id}/${code.code}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-[#D62B27] hover:underline"
                        data-testid={`open-survey-${code.code}`}
                      >
                        Open Survey <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .print\\:break-inside-avoid { break-inside: avoid; }
          .print\\:shadow-none { box-shadow: none; }
          .print\\:border { border: 1px solid #ccc; }
          .print\\:rounded-none { border-radius: 0; }
        }
      `}</style>
    </div>
  );
}
